/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { practiceTestService } from '@/services/practiceTestService'
import { questionService } from '@/services/questionsService'
import { filterCaseStudyQuestionsByQuestionIds } from '@/utils/practiceTestSectionIds'
import { PracticeTestDetail, PracticeTestStatus } from '@/types/practice-test'
import CreateMockExam from '@/features/create-mock-exam'
import MockExamDetail from '@/features/create-mock-exam/detail'

export const Route = createFileRoute(
  '/_authenticated/create-mock-exam/detail/$ptId/$moduleId'
)({
  component: CreateOrDetailWrapper,
})

function CreateOrDetailWrapper() {
  const { ptId, moduleId } = Route.useParams() as {
    ptId: string
    moduleId: string
  }

  const [page, _setPage] = useState(1)
  const { data: pageData, isFetching } = useQuery({
    queryKey: ['practiceTestDetail', ptId, 'paged', page],
    queryFn: () =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size: 10,
        page: page,
      }),
    placeholderData: keepPreviousData,
  })

  const status = pageData?.status?.toLowerCase()?.trim()

  const {
    data: detailPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchPracTestDetail,
  } = useInfiniteQuery({
    queryKey: ['practiceTestDetailRouter', ptId],
    enabled: status === PracticeTestStatus.DRAFT,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size: 10,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      const sections = lastPage?.sections || []
      const firstMeta = (sections?.[0]?.questions as any)?.meta
      const current = Number(firstMeta?.current_page || 1)
      const total = Number(firstMeta?.total_pages || 1)
      return current < total ? current + 1 : undefined
    },
  })

  const pt = (() => {
    const pages = (detailPages?.pages || []) as PracticeTestDetail[]
    if (!pages.length) return undefined
    const base = pages[0]
    const bySection: Record<string, PracticeTestDetail['sections'][number]> = {}
    for (const page of pages) {
      for (const s of page.sections || []) {
        const key = s.name
        const nextChunk = (() => {
          const d: any = (s as any)?.questions?.data
          return Array.isArray(d) ? d : d ? [d] : []
        })()
        if (!bySection[key]) {
          bySection[key] = {
            ...s,
            questions: {
              ...(s.questions as any),
              data: [...nextChunk],
            } as any,
          }
        } else {
          const existing = bySection[key]
          const existingData = (() => {
            const d: any = (existing as any)?.questions?.data
            return Array.isArray(d) ? d : d ? [d] : []
          })()
          bySection[key] = {
            ...existing,
            questions: {
              ...(existing.questions as any),
              data: [...existingData, ...nextChunk],
            } as any,
          }
        }
      }
    }
    return { ...base, sections: Object.values(bySection) } as PracticeTestDetail
  })()
  const practiceTestDetailData =
    status === PracticeTestStatus.DRAFT ? pt : pageData
  const totalsFromDetail = (() => {
    const sections = practiceTestDetailData?.sections || []
    let totalTime = 0
    let totalMarks = 0
    let totalQuestions = 0
    for (const s of sections) {
      totalTime += s.total_time || 0
      totalMarks += s.total_marks || 0
      totalQuestions += s.total_questions || 0
    }
    return { totalTime, totalMarks, totalQuestions }
  })()

  const { data: draftCaseStudyGroups, refetch: refetchDraftCaseStudyGroups } =
    useQuery({
      queryKey: ['draft-case-study-questions', ptId, pt?.sections, detailPages],
      enabled: status === PracticeTestStatus.DRAFT,
      queryFn: async () => {
        const sections = pt?.sections || []
        const result: Record<string, any[]> = {}
        for (const section of sections) {
          const sectionName = section.name
          const csData = section.case_studies?.data as any
          const caseStudies = Array.isArray(csData)
            ? csData
            : csData
              ? [csData]
              : []
          const groups = await Promise.all(
            caseStudies.map(async (cs: any) => {
              try {
                const resp = await questionService.getQuestionByCaseStudy(
                  cs.case_study_id
                )
                // Normalize list from possible shapes
                const list =
                  (resp as any)?.questions ??
                  (resp as any)?.question ??
                  (resp as any)?.data ??
                  []
                const items = filterCaseStudyQuestionsByQuestionIds(
                  Array.isArray(list) ? list : [],
                  cs.question_ids
                )
                let totalTime = 0
                let totalMarks = 0
                for (const q of items) {
                  totalTime += (q as any).time || 0
                  totalMarks += (q as any).marks || 0
                }
                return {
                  case_study_id: cs.case_study_id,
                  items,
                  total_time: totalTime,
                  total_marks: totalMarks,
                  total_questions: items.length,
                }
              } catch (_err) {
                return {
                  case_study_id: cs.case_study_id,
                  items: [],
                  total_time: cs.total_time ?? cs.total_duration ?? 0,
                  total_marks: cs.total_marks ?? cs.total_mark ?? 0,
                  total_questions: 0,
                }
              }
            })
          )
          result[sectionName] = groups
        }
        return result
      },
    })

  useEffect(() => {
    if (
      status === PracticeTestStatus.DRAFT &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage()
    }
  }, [status, hasNextPage, fetchNextPage, isFetchingNextPage])

  const initialDataForEdit = (() => {
    const sections = pt?.sections || []
    const initialSelectedQuestions: Record<string, any[]> = {}
    const initialSelectedCaseStudies: Record<string, any[]> = {}

    for (const section of sections) {
      const sectionName = section.name
      initialSelectedQuestions[sectionName] =
        (section.questions?.data as any[]) || []
      const csData = section.case_studies?.data as any
      const fallback = Array.isArray(csData) ? csData : csData ? [csData] : []
      initialSelectedCaseStudies[sectionName] =
        (draftCaseStudyGroups as any)?.[sectionName] || fallback
    }

    return { initialSelectedQuestions, initialSelectedCaseStudies }
  })()

  if (isFetching && !pageData) {
    return (
      <Flex
        justify='center'
        align='center'
        className='h-[calc(100vh-200px)] w-full'
      >
        <Spinner />
      </Flex>
    )
  }

  if (status === PracticeTestStatus.DRAFT)
    return (
      <CreateMockExam
        refetchData={() => {
          refetchDraftCaseStudyGroups()
          refetchPracTestDetail()
        }}
        initialPracticeTestId={ptId}
        initialPracticeTestName={pt?.pt_name}
        initialGeneralInformation={pt?.general_information}
        status={pt?.status}
        moduleId={moduleId}
        initDuration={pt?.duration}
        initialDurationMinutes={totalsFromDetail.totalTime}
        initialTotalQuestions={totalsFromDetail.totalQuestions}
        initialTotalMarks={totalsFromDetail.totalMarks}
        initialSelectedQuestions={initialDataForEdit.initialSelectedQuestions}
        initialSelectedCaseStudies={
          initialDataForEdit.initialSelectedCaseStudies
        }
      />
    )
  return <MockExamDetail isLoading={isFetching} />
}

export default Route
